import { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Box, Button, Dialog, DialogActions, DialogTitle, Stack, Typography } from '@mui/material';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import dayjs from 'dayjs';
import { format } from 'date-fns';

import { formatDateToTime, formatMillisecondsToTime } from '../../../utils/formatTime';
import { trackerDelete } from '../../../services/trackerService';

import { TimerContext } from '../context/TimerContext';
import RunningTime from './RunningTime';
import { Accordion, AccordionDetails, AccordionSummary, useStyles } from '../styles/taskListStyles';

import formatDate from '../../../utils/formatDate';
import ProgressBar from '../../../components/progressbar/ProgressBar';

export default function TaskList({ item, fetchTaskListData }) {
  const classes = useStyles();
  const { setLoading } = useContext(TimerContext);

  const [expanded, setExpanded] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedId, setSelectedId] = useState();

  const handleDialogOpen = () => setOpenDialog(true);
  const handleDialogClose = () => setOpenDialog(false);

  const deleteTracker = async (id) => {
    try {
      const Tracker = await trackerDelete(id);
      await fetchTaskListData();
      toast.success(Tracker?.data?.message);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error while deleting');
    }
  };

  const onDeleteClick = useCallback((id) => {
    setSelectedId(id);
    handleDialogOpen();
  }, []);

  const handleDelete = async () => {
    try {
      setLoading(true);
      console.log('deleteTracker(selectedId)', deleteTracker(selectedId));
      await deleteTracker(selectedId);
      handleDialogClose();
    } catch (error) {
      toast.error('Oops! Something went wrong while deleting. Please try again later.');
    }
  };

  const handleChange = (_event, newExpanded) => setExpanded(newExpanded);

  const isToday = item?.checkDate === new Date().toDateString();

  let workedHrs;

  if (isToday) {
    workedHrs = <RunningTime />;
  } else if (item?.totalTime instanceof Date) {
    workedHrs = formatDateToTime(item.totalTime);
  } else {
    workedHrs = '00:00:00';
  }

  return (
    <>
      <section>
        <Accordion sx={{ mb: 0.5 }} expanded={expanded} onChange={handleChange}>
          <AccordionSummary>
            <div className={classes.summaryContainer}>
              <Typography>{isToday ? `Today (${item?.checkDate})` : item?.checkDate}</Typography>
              <Typography>
                Total: <b>{workedHrs}</b>
              </Typography>
            </div>
          </AccordionSummary>
          {item?.tasks?.map((data, index) => (
            <AccordionDetails key={index} style={{ padding: 0, marginBottom: '8px' }}>
              <Accordion expanded>
                <AccordionSummary expandIcon={null}>
                  <div className={classes.taskContainer}>
                    <div className={classes.taskCountContainer}>
                      <Typography className={classes.taskCount}>{data.TimeTracker.length}</Typography>
                    </div>

                    <TaskContent data={data} />
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  {data?.TimeTracker.map((tracker) => (
                    <TrackerItem key={tracker?.id} tracker={tracker} onDeleteClick={onDeleteClick} />
                  ))}
                </AccordionDetails>
              </Accordion>
            </AccordionDetails>
          ))}
        </Accordion>
      </section>
      <Dialog open={openDialog} onClose={handleDialogClose} aria-labelledby="delete-dialog-title">
        <DialogTitle id="delete-dialog-title">Are you sure you want to delete this?</DialogTitle>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const TaskContent = ({ data }) => {
  const classes = useStyles();
  return (
    <Stack direction="column" sx={{ width: '100%' }}>
      <div className={classes.taskContent}>
        <Box sx={{ width: '50%' }}>
          <Typography className={classes.taskTitle}>{data.taskName}</Typography>
          {data?.projects?.projectName && (
            <Box>
              <Typography className={classes.subText}>{data?.projects?.projectName}</Typography>
            </Box>
          )}
        </Box>
        <div className={classes.timeWrapper}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
            <Typography fontSize={'12px'}>
              {formatDate(data.startDate)} - {formatDate(data.endDate)}
            </Typography>

            <Typography className={classes.timeText}>{formatMillisecondsToTime(data?.taskTotalTime || 0)}</Typography>
          </div>
        </div>
      </div>
      <Box sx={{ mb: 0.5 }}>
        <ProgressBar
          maxValue={data?.plannedHours}
          value={Number(data?.consumedHours).toFixed(2)}
          plannedValue={data?.plannedHours}
          fontSize="12px"
          status={data?.status}
        />
      </Box>
    </Stack>
  );
};

const TrackerItem = ({ tracker, onDeleteClick }) => {
  const classes = useStyles();

  const diffTime = new Date(tracker?.stopTime) - new Date(tracker?.startTime);
  const startTime = dayjs(tracker?.startTime).format('hh:mm:ss');
  const stopTime = dayjs(tracker?.stopTime)?.format('hh:mm:ss') ?? '';

  const handleDeleteClick = () => onDeleteClick(tracker?.id);
  return (
    <div className={classes.historyContainer}>
      <div className={classes?.trackerItem}>
        <Typography className={classes.trackerHistoryText}>
          {startTime} - {stopTime ?? ''}
        </Typography>

        {/* <DeleteForeverIcon className={classes.deleteIcon} onClick={handleDeleteClick} /> */}
      </div>
      <div style={{ width: '55px' }}>
        <Typography className={classes.trackerHistoryText}>{formatMillisecondsToTime(diffTime)}</Typography>
      </div>
    </div>
  );
};

TaskList.propTypes = {
  item: PropTypes.object.isRequired,
  fetchTaskListData: PropTypes.func.isRequired,
};

TaskContent.propTypes = {
  data: PropTypes.object.isRequired,
};

TrackerItem.propTypes = {
  tracker: PropTypes.object.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
};
